/**
 * How the OAuth completion tab tells the page that started it that the round
 * trip is over.
 *
 * The two tabs cannot talk any other way. `openAuthorizationWindow` nulls
 * `opener` on purpose, so there is no window reference to `postMessage` through
 * in either direction — and restoring one to make this easier would hand the
 * provider's page, and anything it redirects to, a handle on the authenticated
 * console. A `BroadcastChannel` is same-origin by construction and needs no
 * such handle.
 *
 * WHAT TRAVELS IS DELIBERATELY NOTHING. The message is one frozen string and
 * carries no outcome, no connection id, no error code, no `state` and no
 * token. Anything a listener learned from a broadcast would be a fact asserted
 * by an unauthenticated page, and the listener is an authenticated one — so the
 * signal says only "ask the server again", and the server's answer is the
 * authority. That also means a same-origin script cannot use this channel to
 * plant a connection that does not exist.
 *
 * Nothing is persisted: no `localStorage`, no `sessionStorage`. A signal that
 * outlived the tab that sent it would announce a round trip on the NEXT visit.
 */

/** One channel for the whole surface — it is provider-neutral, like the flow. */
const CHANNEL_NAME = "playbookai.integration-oauth";

/**
 * The only message this channel recognises, and it is VERSIONED.
 *
 * Accepted by exact equality below, so a future message shape bumps the suffix
 * rather than teaching this listener to parse. An old tab left open across a
 * deploy then ignores a signal it cannot interpret, instead of guessing.
 */
const COMPLETION_SIGNAL = "playbookai.integration-oauth.completed/1";

/** Exact match, never a prefix or a shape test. Exported so tests can pin it. */
export function isIntegrationOauthCompletionSignal(data: unknown): boolean {
  return data === COMPLETION_SIGNAL;
}

/**
 * Announce that a round trip finished — whatever its outcome.
 *
 * Success and failure broadcast the SAME signal, because the signal is not the
 * result: the listener refetches its own authenticated APIs and finds out
 * there. One message shape also means a failure cannot be presented as a
 * success by whoever crafted the URL that reached the completion page.
 */
export function publishIntegrationOauthCompletion(): void {
  const channel = openChannel();
  if (channel === null) return;
  try {
    channel.postMessage(COMPLETION_SIGNAL);
  } finally {
    // `postMessage` has already queued the delivery task for every other
    // channel object by the time it returns, so closing here cannot lose it.
    channel.close();
  }
}

/**
 * Listen for a completion, returning an unsubscribe.
 *
 * An unsubscribe is returned even where `BroadcastChannel` does not exist, so a
 * caller's cleanup path is the same either way. A browser without it is not a
 * broken flow — the caller's bounded poll is what covers both that case and a
 * signal that never arrives.
 */
export function subscribeToIntegrationOauthCompletion(
  onCompleted: () => void,
): () => void {
  const channel = openChannel();
  if (channel === null) return () => undefined;

  const handler = (event: MessageEvent): void => {
    if (isIntegrationOauthCompletionSignal(event.data)) onCompleted();
  };
  channel.addEventListener("message", handler);

  return () => {
    channel.removeEventListener("message", handler);
    channel.close();
  };
}

/**
 * `BroadcastChannel` is unavailable in some embedded webviews, and constructing
 * one can throw where storage access is partitioned away. Neither is a reason
 * to break the page that called us, so both collapse to "no channel".
 */
function openChannel(): BroadcastChannel | null {
  if (typeof BroadcastChannel === "undefined") return null;
  try {
    return new BroadcastChannel(CHANNEL_NAME);
  } catch {
    return null;
  }
}
